import { z } from 'zod';

export const ClickedProfilingEventSchema: z.ZodObject<
	{
		event: z.ZodLiteral<'click'>;
	},
	'strip'
> = z
	.object({
		event: z.literal('click'),
	})
	.strip();

export const NavigationTimingProfilingEventSchema: z.ZodObject<
	{
		duration: z.ZodNumber;
		event: z.ZodLiteral<'navigation-timing'>;
		transferSize: z.ZodNumber;
	},
	'strict'
> = z
	.object({
		event: z.literal('navigation-timing'),
		transferSize: z.number().nonnegative().finite(),
		duration: z.number().nonnegative().finite(),
	})
	.strict();

const initiatorTypeValues = z.enum([
	'script',
	'fetch',
	'css',
	'link',
	'iframe',
	'img',
	'image',
	'xmlhttprequest',
	'other',
]);
export const ResourceTimingProfilingEventSchema: z.ZodObject<
	{
		duration: z.ZodNumber;
		event: z.ZodLiteral<'resource-timing'>;
		initiatorType: z.ZodPipeline<
			z.ZodPipeline<
				z.ZodString,
				z.ZodCatch<
					z.ZodEnum<
						['script', 'fetch', 'css', 'link', 'iframe', 'img', 'image', 'xmlhttprequest', 'other']
					>
				>
			>,
			z.ZodString
		>;
		start: z.ZodNumber;
		transferSize: z.ZodNumber;
	},
	'strict'
> = z
	.object({
		event: z.literal('resource-timing'),
		start: z.number().nonnegative().finite(),
		duration: z.number().nonnegative().finite(),
		transferSize: z.number().nonnegative().finite(),
		initiatorType: z
			.string()
			.pipe(initiatorTypeValues.catch(initiatorTypeValues.Values.other))
			.pipe(z.string()),
	})
	.strict();

export const LayoutShiftProfilingEventSchema: z.ZodObject<
	{
		event: z.ZodLiteral<'dcls-score'>;
		inputFlag: z.ZodBoolean;
		score: z.ZodNumber;
		timestamp: z.ZodNumber;
	},
	'strict'
> = z
	.object({
		event: z.literal('dcls-score'),
		score: z.number().nonnegative().finite(),
		timestamp: z.number().nonnegative().finite(),
		inputFlag: z.boolean(),
	})
	.strict();

export const FCPProfilingEventSchema: z.ZodObject<
	{
		event: z.ZodLiteral<'fcp-snapshot'>;
		start: z.ZodNumber;
	},
	'strict'
> = z
	.object({
		event: z.literal('fcp-snapshot'),
		start: z.number().nonnegative().finite(),
	})
	.strict();

export const LCPProfilingEventSchema: z.ZodObject<
	{
		event: z.ZodLiteral<'lcp-snapshot'>;
		size: z.ZodNumber;
		start: z.ZodNumber;
	},
	'strict'
> = z
	.object({
		event: z.literal('lcp-snapshot'),
		size: z.number().nonnegative().finite(),
		start: z.number().nonnegative().finite(),
	})
	.strict();

const serialisableValueSchema: z.ZodType<unknown> = z.lazy(() =>
	z.union([
		z.string(),
		z.number().finite(),
		z.boolean(),
		z.null(),
		z.array(serialisableValueSchema),
		z.record(z.string(), serialisableValueSchema),
	]),
);

export const UFOFrameEventSchema: z.ZodObject<
	{
		event: z.ZodLiteral<'ufo-event'>;
		name: z.ZodString;
		payload: z.ZodRecord<z.ZodString, z.ZodType<unknown, z.ZodTypeDef, unknown>>;
	},
	'strict'
> = z
	.object({
		event: z.literal('ufo-event'),
		name: z.string().min(1).max(256),
		payload: z.record(z.string(), serialisableValueSchema),
	})
	.strict();

export const AppFrameProfilingEventSchema: z.ZodDiscriminatedUnion<
	'event',
	[
		z.ZodObject<
			{
				event: z.ZodLiteral<'click'>;
			},
			'strip',
			z.ZodTypeAny,
			{
				event: 'click';
			},
			{
				event: 'click';
			}
		>,
		z.ZodObject<
			{
				duration: z.ZodNumber;
				event: z.ZodLiteral<'navigation-timing'>;
				transferSize: z.ZodNumber;
			},
			'strict',
			z.ZodTypeAny,
			{
				event: 'navigation-timing';
				duration: number;
				transferSize: number;
			},
			{
				event: 'navigation-timing';
				duration: number;
				transferSize: number;
			}
		>,
		z.ZodObject<
			{
				duration: z.ZodNumber;
				event: z.ZodLiteral<'resource-timing'>;
				initiatorType: z.ZodPipeline<
					z.ZodPipeline<
						z.ZodString,
						z.ZodCatch<
							z.ZodEnum<
								[
									'script',
									'fetch',
									'css',
									'link',
									'iframe',
									'img',
									'image',
									'xmlhttprequest',
									'other',
								]
							>
						>
					>,
					z.ZodString
				>;
				start: z.ZodNumber;
				transferSize: z.ZodNumber;
			},
			'strict',
			z.ZodTypeAny,
			{
				start: number;
				event: 'resource-timing';
				duration: number;
				transferSize: number;
				initiatorType: string;
			},
			{
				start: number;
				event: 'resource-timing';
				duration: number;
				transferSize: number;
				initiatorType: string;
			}
		>,
		z.ZodObject<
			{
				event: z.ZodLiteral<'dcls-score'>;
				inputFlag: z.ZodBoolean;
				score: z.ZodNumber;
				timestamp: z.ZodNumber;
			},
			'strict',
			z.ZodTypeAny,
			{
				event: 'dcls-score';
				inputFlag: boolean;
				score: number;
				timestamp: number;
			},
			{
				event: 'dcls-score';
				inputFlag: boolean;
				score: number;
				timestamp: number;
			}
		>,
		z.ZodObject<
			{
				event: z.ZodLiteral<'fcp-snapshot'>;
				start: z.ZodNumber;
			},
			'strict',
			z.ZodTypeAny,
			{
				start: number;
				event: 'fcp-snapshot';
			},
			{
				start: number;
				event: 'fcp-snapshot';
			}
		>,
		z.ZodObject<
			{
				event: z.ZodLiteral<'lcp-snapshot'>;
				size: z.ZodNumber;
				start: z.ZodNumber;
			},
			'strict',
			z.ZodTypeAny,
			{
				start: number;
				size: number;
				event: 'lcp-snapshot';
			},
			{
				start: number;
				size: number;
				event: 'lcp-snapshot';
			}
		>,
		z.ZodObject<
			{
				event: z.ZodLiteral<'ufo-event'>;
				name: z.ZodString;
				payload: z.ZodRecord<z.ZodString, z.ZodType<unknown, z.ZodTypeDef, unknown>>;
			},
			'strict',
			z.ZodTypeAny,
			{
				name: string;
				event: 'ufo-event';
				payload: Record<string, unknown>;
			},
			{
				name: string;
				event: 'ufo-event';
				payload: Record<string, unknown>;
			}
		>,
	]
> = z.discriminatedUnion('event', [
	ClickedProfilingEventSchema,
	NavigationTimingProfilingEventSchema,
	ResourceTimingProfilingEventSchema,
	LayoutShiftProfilingEventSchema,
	FCPProfilingEventSchema,
	LCPProfilingEventSchema,
	UFOFrameEventSchema,
]);

export type FrameProfilingEventType = z.infer<typeof AppFrameProfilingEventSchema>;
